import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Layers, Brain, BookOpen, BarChart3 } from 'lucide-react';

export default function Portfolio() {
  const projects = [
    {
      id: 'agency-site',
      name: 'Code Credence Brand Platform',
      category: 'Enterprise UI/UX & Web',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
      description: 'A modular, high-performance showcase platform built with React and custom animations to establish digital agency credentials and drive leads.',
      icon: Layers,
      color: 'from-[#8B6B4A]/20 to-[#C8A97E]/10',
      tech: ['React', 'Tailwind CSS', 'Framer Motion', 'Vite', 'PostCSS']
    },
    {
      id: 'ai-agent',
      name: 'Conversational AI Agent',
      category: 'AI & Automation Systems',
      image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80',
      description: 'An automated customer assistance bot utilizing deep neural networks to resolve customer issues, qualify leads, and schedule calendar sessions.',
      icon: Brain,
      color: 'from-[#C8A97E]/20 to-[#D6C2A8]/10',
      tech: ['Next.js', 'FastAPI', 'OpenAI API', 'Pinecone', 'Docker']
    },
    {
      id: 'lms-suite',
      name: 'Greenwood EdTech Suite',
      category: 'LMS & Management systems',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
      description: 'A full-scale portals ecosystem connecting 2,500 students, teachers, and admins. Supports online classes, grading rubrics, and automated admission.',
      icon: BookOpen,
      color: 'from-[#8B6B4A]/20 to-[#D6C2A8]/10',
      tech: ['React', 'Node.js', 'Express', 'MongoDB', 'AWS S3']
    },
    {
      id: 'cloud-saas',
      name: 'CloudSync SaaS Suite',
      category: 'SaaS Platforms & Analytics',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      description: 'Multi-tenant subscription analytics dashboard tracking subscription health, billing cohorts, churn metrics, and data pipeline logs in real time.',
      icon: BarChart3,
      color: 'from-[#C8A97E]/20 to-[#8B6B4A]/10',
      tech: ['React', 'Golang', 'PostgreSQL', 'Redis', 'Kubernetes']
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-[#F8F5F0]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-xs font-semibold tracking-widest uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#8B6B4A] to-[#C8A97E] font-poppins mb-3 block" data-testid="portfolio-label">
            Our Work
          </span>
          <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight" data-testid="portfolio-heading">
            SaaS & Full-Stack Projects
          </h2>
          <p className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4" data-testid="portfolio-description">
            Explore our ecosystem of deployed applications, automations, and digital platforms engineered for scaling results.
          </p>
        </div>

        {/* Portfolio Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-2 gap-8"
          data-testid="portfolio-grid"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={cardVariants}
              className="group relative rounded-3xl overflow-hidden bg-white/40 border border-[#8B6B4A]/10 hover:border-[#8B6B4A]/30 hover:shadow-lg transition-all duration-500 flex flex-col justify-between"
              data-testid={`portfolio-project-card-${index}`}
            >
              
              <div>
                {/* Project Image Wrapper */}
                <div className="relative h-64 overflow-hidden border-b border-[#8B6B4A]/10">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                    loading="lazy"
                    data-testid={`portfolio-image-${index}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#F8F5F0] to-transparent opacity-90" />
                  
                  {/* Category Tag */}
                  <span className="absolute top-4 left-4 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-white/90 border border-[#8B6B4A]/10 text-[#3E2F24] font-poppins shadow-sm">
                    {project.category}
                  </span>
                </div>

                {/* Card Content */}
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-[#8B6B4A]/5 text-[#8B6B4A]">
                      <project.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-2xl font-bold font-space text-[#3E2F24]" data-testid={`portfolio-project-name-${index}`}>
                      {project.name}
                    </h3>
                  </div>

                  <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed mb-6" data-testid={`portfolio-problem-${index}`}>
                    {project.description}
                  </p>
                </div>
              </div>

              {/* Card Footer: Tech tags */}
              <div className="px-8 pb-8 pt-2">
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 rounded-full text-[11px] font-medium bg-[#8B6B4A]/5 border border-[#8B6B4A]/5 text-[#6B625B] font-poppins"
                      data-testid={`portfolio-tech-${tech.toLowerCase()}`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="w-full h-px bg-[#8B6B4A]/10 mb-4" />

                <div className="flex justify-end">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6B625B] group-hover:text-[#3E2F24] font-poppins transition-colors select-none">
                    Engineering Walkthrough
                    <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>

            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
}